import type {
  FileResponse,
  GetItemAssignmentListRequest,
  GetItemAssignmentListRequestV2,
  GetListResponseOfItemAssignmentSimpleResponse,
  ItemAssignmentRequest,
  ItemAssignmentResponse,
  ProcessEmployeeRequestForItemsRequest,
  ProcessEmployeeRequestForItemsResponse,
} from 'src/api/ProactApiClient';
import {itemAssignmentApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

export interface ItemAssignmentPdfArgs {
  id: number;
  timeZone: string;
}

export const itemAssignmentApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getItemAssignmentList: builder.query<
      GetListResponseOfItemAssignmentSimpleResponse,
      GetItemAssignmentListRequest
    >({
      queryFn: clientQuery(params => itemAssignmentApiClient.list(params)),
      providesTags: ['ItemAssignments'],
    }),
    getItemAssignmentListV2: builder.query<
      GetListResponseOfItemAssignmentSimpleResponse,
      GetItemAssignmentListRequestV2
    >({
      queryFn: clientQuery(params => itemAssignmentApiClient.listV2(params)),
      providesTags: ['ItemAssignments'],
    }),
    getItemAssignment: builder.query<ItemAssignmentResponse, number>({
      queryFn: clientQuery(id => itemAssignmentApiClient.get(id)),
      providesTags: ['ItemAssignments'],
    }),
    createItemAssignment: builder.mutation<
      ItemAssignmentResponse,
      ItemAssignmentRequest
    >({
      queryFn: clientQuery(request => itemAssignmentApiClient.create(request)),
      invalidatesTags: ['ItemAssignments'],
    }),
    updateItemAssignment: builder.mutation<
      ItemAssignmentResponse,
      ItemAssignmentRequest
    >({
      queryFn: clientQuery(request => itemAssignmentApiClient.update(request)),
      invalidatesTags: ['ItemAssignments'],
    }),
    deleteItemAssignment: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => itemAssignmentApiClient.delete(id)),
      invalidatesTags: ['ItemAssignments'],
    }),
    processEmployeeRequestForItems: builder.mutation<
      ProcessEmployeeRequestForItemsResponse,
      ProcessEmployeeRequestForItemsRequest
    >({
      queryFn: clientQuery(request =>
        itemAssignmentApiClient.processEmployeeRequestForItems(request)
      ),
      invalidatesTags: ['ItemAssignments'],
    }),
    getIssuanceCertificatePdf: builder.mutation<
      FileResponse | null,
      ItemAssignmentPdfArgs
    >({
      queryFn: clientQuery(({id, timeZone}: ItemAssignmentPdfArgs) =>
        itemAssignmentApiClient.getIssuanceCertificatePdf(id, timeZone)
      ),
    }),
    downloadZfGlassesVoucher: builder.mutation<
      FileResponse | null,
      ItemAssignmentPdfArgs
    >({
      queryFn: clientQuery(({id, timeZone}: ItemAssignmentPdfArgs) =>
        itemAssignmentApiClient.downloadZfGlassesVoucher(id, timeZone)
      ),
    }),
    downloadZfBootsVoucher: builder.mutation<
      FileResponse | null,
      ItemAssignmentPdfArgs
    >({
      queryFn: clientQuery(({id, timeZone}: ItemAssignmentPdfArgs) =>
        itemAssignmentApiClient.downloadZfBootsVoucher(id, timeZone)
      ),
    }),
  }),
});

export const {
  useGetItemAssignmentListQuery,
  useLazyGetItemAssignmentListQuery,
  useGetItemAssignmentListV2Query,
  useGetItemAssignmentQuery,
  useCreateItemAssignmentMutation,
  useUpdateItemAssignmentMutation,
  useDeleteItemAssignmentMutation,
  useProcessEmployeeRequestForItemsMutation,
  useGetIssuanceCertificatePdfMutation,
  useDownloadZfGlassesVoucherMutation,
  useDownloadZfBootsVoucherMutation,
} = itemAssignmentApi;
